/**
 * Media type detection utilities
 * Used for detecting media type from URL extensions when contentType is not available
 */

export type MediaType = 'image' | 'video' | 'audio';

// Canonical media extension sets — single source of truth across app.
// NOTE: `mpeg` listed under AUDIO because OS MIME-mapping labels .mpeg files
// as video/mpeg, but Suno and similar tools output MPEG-1 Layer III audio
// with .mpeg extension. Treat .mpeg as audio by default.
export const AUDIO_EXTENSIONS = ['mp3', 'mpeg', 'mpga', 'wav', 'ogg', 'oga', 'flac', 'm4a', 'aac', 'weba'];
export const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v', 'mpg'];
export const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'tiff', 'heic', 'heif', 'avif'];
// 3D models are intentionally not part of this shared `MediaType`: model input uploads
// use a slot-local union in BigSlotsInputMediaWidget. This canonical set is shared by
// slot admission, storage/download naming, URL detection, and preview routing.
export const MODEL_EXTENSIONS = ['glb', 'gltf', 'obj', 'fbx', 'stl', 'usdz'];
const MODEL_MIME_EXTENSIONS: Readonly<Record<string, string>> = {
  'model/gltf-binary': 'glb',
  'model/gltf+json': 'gltf',
  'model/obj': 'obj',
  'model/fbx': 'fbx',
  'model/stl': 'stl',
  'model/vnd.usdz+zip': 'usdz',
};
// Subset `<model-viewer>` can actually render through `src`: the glTF family only.
// `.usdz` is iOS AR Quick Look (`ios-src`) and `.obj`/`.fbx`/`.stl` are not supported
// at all — all four are stored, listed and downloaded normally, just not previewed.
export const VIEWABLE_MODEL_EXTENSIONS = ['glb', 'gltf'];

const AUDIO_EXT_SET = new Set(AUDIO_EXTENSIONS);
const VIDEO_EXT_SET = new Set(VIDEO_EXTENSIONS);
const IMAGE_EXT_SET = new Set(IMAGE_EXTENSIONS);

/** Build HTML `accept` attribute value for file inputs: MIME wildcards + explicit ext fallbacks. */
export function buildAcceptAttr(types: MediaType[]): string {
  const parts: string[] = [];
  for (const t of types) {
    parts.push(`${t}/*`);
    const exts = t === 'audio' ? AUDIO_EXTENSIONS : t === 'video' ? VIDEO_EXTENSIONS : IMAGE_EXTENSIONS;
    for (const e of exts) parts.push(`.${e}`);
  }
  return parts.join(',');
}

/** Extract lowercase extension from filename or URL path (no query/hash). */
export function getExtension(nameOrUrl: string): string {
  if (!nameOrUrl) return '';
  const clean = nameOrUrl.split('?')[0].split('#')[0];
  return clean.split('.').pop()?.toLowerCase() ?? '';
}

/** Resolve a supported 3D model extension from a filename or MIME fallback. */
export function getModelExtensionFromNameAndMime(filename: string, mime?: string): string | null {
  const extension = getExtension(filename);
  if (MODEL_EXTENSIONS.includes(extension)) return extension;

  // A real filename extension is authoritative for format restrictions. MIME
  // is only a fallback for extensionless files, matching server upload naming.
  const basename = filename.split(/[\\/]/).pop() ?? '';
  if (basename.includes('.')) return null;

  const normalizedMime = mime?.split(';', 1)[0].trim().toLowerCase() ?? '';
  return MODEL_MIME_EXTENSIONS[normalizedMime] ?? null;
}

/**
 * Classify a file exactly as the Slots widgets do when admitting and counting it.
 * MIME prefixes win for image/video/audio; model extensions win because browsers
 * commonly report model uploads with an empty or generic MIME.
 */
export function getSlotMediaTypeFromNameAndMime(
  filename: string,
  mime?: string
): MediaType | 'model' {
  const normalizedMime = mime?.split(';', 1)[0].trim().toLowerCase() ?? '';
  if (getModelExtensionFromNameAndMime(filename, normalizedMime)) {
    return 'model';
  }
  if (normalizedMime.startsWith('image/')) return 'image';
  if (normalizedMime.startsWith('video/')) return 'video';
  if (normalizedMime.startsWith('audio/')) return 'audio';
  return 'image';
}

/** Detect media type from filename + optional MIME. Extension wins for audio (OS mis-labels .mpeg as video). */
export function detectMediaTypeFromNameAndMime(filename: string, mime?: string): MediaType | null {
  const ext = getExtension(filename);
  if (AUDIO_EXT_SET.has(ext)) return 'audio';
  if (mime) {
    // Reject SVG — XSS risk
    if (mime.includes('svg')) return null;
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('audio/')) return 'audio';
    if (mime.startsWith('video/')) return 'video';
  }
  if (VIDEO_EXT_SET.has(ext)) return 'video';
  if (IMAGE_EXT_SET.has(ext)) return 'image';
  return null;
}

/**
 * Detect media type from URL extension
 * Used as fallback when contentType is not available or incorrect
 *
 * @param url - The URL to analyze
 * @returns MediaType - 'image', 'video', or 'audio'
 */
export function getMediaTypeFromUrl(url: string): MediaType {
  if (!url) return 'image';

  // Remove query parameters and hash fragments, then get extension
  const cleanUrl = url.split('?')[0].split('#')[0];
  const extension = cleanUrl.split('.').pop()?.toLowerCase() || '';

  if (VIDEO_EXTENSIONS.includes(extension)) return 'video';
  if (AUDIO_EXTENSIONS.includes(extension)) return 'audio';

  return 'image';
}

/**
 * True when a URL points at a 3D model file.
 *
 * Kept separate from `getMediaTypeFromUrl` because 3D is not part of the shared
 * `MediaType`; model input uploads use a slot-local type. This remains the render
 * fallback for generations whose `content_type` is missing or stale.
 */
export function is3DModelUrl(url?: string | null): boolean {
  if (!url) return false;
  const cleanUrl = url.split('?')[0].split('#')[0];
  const extension = cleanUrl.split('.').pop()?.toLowerCase() || '';
  return MODEL_EXTENSIONS.includes(extension);
}

/**
 * True when a URL carries a model extension `<model-viewer>` cannot render.
 *
 * Deliberately keyed off the *known-unsupported* extensions rather than
 * `!VIEWABLE_MODEL_EXTENSIONS.includes(ext)`: provider URLs (Dify `file-preview`,
 * presigned CDN paths) frequently carry no extension at all, and those are
 * overwhelmingly GLB — blocking them would swap a working viewer for a dead end.
 * An unknown extension therefore still gets a load attempt; only a positive
 * `.obj` / `.fbx` / `.stl` / `.usdz` short-circuits it, so the user gets an
 * honest "no preview for this format" instead of a red "the file is corrupted"
 * after downloading the whole model for nothing.
 */
export function isUnviewableModelUrl(url?: string | null): boolean {
  if (!url) return false;
  const ext = getExtension(url);
  return MODEL_EXTENSIONS.includes(ext) && !VIEWABLE_MODEL_EXTENSIONS.includes(ext);
}

/**
 * True when a generation's content type denotes a 3D model.
 *
 * Two shapes reach the UI for the same asset: gallery/history rows carry the
 * category-derived `'3d'`, while a freshly dispatched slot gets whatever
 * `contentTypes[0]` held — a `model/*` MIME from `processContentUrl`. Both must
 * be accepted or download naming and the render branch disagree on one asset.
 * Single definition so the four render surfaces and `downloadNaming` cannot drift.
 */
export function is3DContentType(contentType?: string | null): boolean {
  return contentType === '3d' || !!contentType?.startsWith('model/');
}

/**
 * True when a URL carries an image/video/audio extension, i.e. an extension
 * that positively identifies the asset as NOT a 3D model.
 */
function hasDecisiveNonModelExtension(url?: string | null): boolean {
  if (!url) return false;
  const cleanUrl = url.split('?')[0].split('#')[0];
  const extension = cleanUrl.split('.').pop()?.toLowerCase() || '';
  return (
    IMAGE_EXTENSIONS.includes(extension) ||
    extension === 'svg' ||
    VIDEO_EXTENSIONS.includes(extension) ||
    AUDIO_EXTENSIONS.includes(extension)
  );
}

/**
 * Decide whether ONE asset should render as 3D.
 *
 * `content_type` has GENERATION granularity: a single column per generation,
 * derived from the workflow category ('3D' → '3d'). A 3D provider result
 * routinely ships companions next to the mesh — fal's hunyuan-3d returns the
 * model plus a texture PNG, a preview PNG and an .mtl — and all of them inherit
 * that one '3d'. The URL extension has PER-ASSET granularity.
 *
 * So the two signals are ranked rather than OR'd (the old
 * `is3DContentType(ct) || is3DModelUrl(url)` could only ever ADD 3D, never
 * withdraw it, so a texture PNG rendered as a 3D placeholder):
 *
 * 1. a model extension → 3D, whatever the content type says (covers a stale or
 *    missing content_type);
 * 2. an image/video/audio extension → NOT 3D, overriding the generation type;
 * 3. otherwise → the generation content type. Extensionless provider URLs
 *    (Dify `file-preview`, presigned CDN paths) land here and are overwhelmingly
 *    GLB, so this fallback must stay — see `isUnviewableModelUrl`.
 *
 * Companion files with a 3D-adjacent but unlisted extension (.mtl) are NOT
 * decisive and correctly stay with the generation type.
 */
export function resolveIs3D(contentType?: string | null, url?: string | null): boolean {
  if (is3DModelUrl(url)) return true;
  if (hasDecisiveNonModelExtension(url)) return false;
  return is3DContentType(contentType);
}

// 'svg' allowed in URL ext matcher for existing assets — upload paths reject via MIME check.
const ALL_MEDIA_EXTENSIONS = [...IMAGE_EXTENSIONS, 'svg', ...VIDEO_EXTENSIONS, ...AUDIO_EXTENSIONS, ...MODEL_EXTENSIONS];

/**
 * Extract file extension from URL, falling back to a default
 * Uses the actual extension from the URL path (e.g. .png, .webp, .mp4)
 */
export function getExtensionFromUrl(url: string, fallback = 'jpg'): string {
  if (!url) return fallback;
  const cleanUrl = url.split('?')[0].split('#')[0];
  const ext = cleanUrl.split('.').pop()?.toLowerCase() || '';
  if (ALL_MEDIA_EXTENSIONS.includes(ext)) {
    return ext;
  }
  return fallback;
}

export function categorizeGalleryByMediaType<T extends { mediaType: MediaType }>(
  galleryUrls: T[]
): { images: T[]; videos: T[]; audios: T[] } {
  return {
    images: galleryUrls.filter(g => g.mediaType === 'image'),
    videos: galleryUrls.filter(g => g.mediaType === 'video'),
    audios: galleryUrls.filter(g => g.mediaType === 'audio')
  };
}
